/**
 * THE SCREEN STATE MACHINE AND THE POLL LADDER — pure, and the reason the
 * seven states of spec 16.2 are testable at all.
 *
 * ══ WHY THIS IS A PURE FUNCTION AND NOT A `useState` SOUP ══════════════════
 *
 * Every state the operator can land in — empty, loading, running, stopped,
 * refused, declined, answered, and five DISTINCT error renderings — is decided
 * HERE, by `deriveScreenState`, from an observation object. The React page
 * switches on the result and draws it. That split is what lets
 * `tests/unit/admin/agent-turn-state.test.ts` drive the whole matrix under a
 * `node` test environment with no jsdom, and it is why "the 429 state ships as
 * an untestable placeholder" is a statement about ONE arm rather than about
 * the whole screen.
 *
 * ══ THE LADDER IS WRITTEN FOR A 100-SECOND TURN, NOT A 30-SECOND ONE ═══════
 *
 * Spec 14.7: DEV and PREDEV egress through a NAT INSTANCE (a t4g.nano when this
 * was measured; a t4g.micro since 2026-09-15) and one Anthropic round trip
 * there was MEASURED above 25 s, so a 3–4 iteration turn
 * runs 75–140 s. A ladder tuned to prod latency would make every DEV turn look
 * broken. The server already holds each GET for up to 20 s (spec 14.2), so
 * these delays sit BETWEEN holds; they exist to stop a client hammering the
 * shared PROD WAF rate limit of 200 req/min per IP for the ENTIRE API.
 */

import type { TurnPollResponse, TurnDecline, TurnResultPayload } from './protocol';

/** Spec 14.7's ladder, verbatim. */
export function nextPollDelayMs(elapsedMs: number): number {
  if (elapsedMs < 5_000) return 700;
  if (elapsedMs < 20_000) return 1_500;
  if (elapsedMs < 120_000) return 3_000;
  return 5_000;
}

/**
 * WHEN THIS SCREEN STOPS WAITING — AFTER THE SERVER HAS SAID WHAT HAPPENED.
 *
 * Spec 14.7 said 300 s. That was shorter than the server's own bounds: the
 * worker's soft deadline is 480 s off PROD, and a worker that dies can only be
 * PROVED dead 755 s after the question (`RUNNING_LOST_AFTER_MS`,
 * `backend/src/agent/transport/turn-liveness.ts`). At 300 s the screen
 * therefore gave up on turns that were still running, and said "may still
 * finish" about turns whose worker was already dead (#2964 row 8).
 *
 * Now the server ends every turn by 755 s: the poll GET marks a turn with no
 * possible worker `failed` with `worker_lost` or `never_started`. This value is
 * that bound plus one 20 s poll hold, the longest poll delay (5 s) and a margin,
 * so the server's verdict always arrives first. Reaching it means this screen
 * lost contact with the server, not that the turn might still be running.
 * `tests/unit/lint/agent-turn-liveness-bounds.test.ts` pins the order.
 */
export const POLL_GIVE_UP_MS = 810_000;

/**
 * When the inline `SlowTurnHint` appears (spec 16.2 state 5). NON-PROD ONLY —
 * on prod a 30-second turn is a real problem and must not be explained away by
 * a banner about NAT instances.
 */
export const SLOW_TURN_HINT_MS = 30_000;

/** The five error renderings of spec 16.2 state 4. Never one generic banner. */
export type TurnErrorVariant =
  | 'transport'
  | 'turn_failed'
  | 'timed_out'
  | 'forbidden'
  | 'rate_limited';

export interface TransportFailure {
  /** HTTP status, or `null` when the request never completed (offline, CORS). */
  status: number | null;
  /** The short code the front door answers with, e.g. `turn_not_found`. */
  code: string;
}

export type AgentScreenState =
  | { kind: 'empty' }
  | { kind: 'loading' }
  | {
      kind: 'running';
      phase: TurnPollResponse['phase'];
      iteration: number;
      toolCallsDone: number;
      tool: string | null;
      elapsedMs: number;
      slowHint: boolean;
      environment: string;
    }
  | { kind: 'stopped' }
  | { kind: 'refused'; reasonCode: string; result: TurnResultPayload }
  | { kind: 'declined'; decline: TurnDecline; result: TurnResultPayload }
  /**
   * `incomplete` is TRUE when the worker stopped on its deadline or iteration
   * cap. The backend emits `deadline_exceeded` as a NORMAL done result — decline
   * null, gate_allow true, and whatever tool calls finished before the break —
   * so without this flag `terminalState` fell straight through to `answered`
   * and the operator saw a card indistinguishable from a finished one, while a
   * screen reader was told, in words, "Answer complete."
   *
   * A partial aggregate presented as a whole one is the same failure class the
   * prose-fidelity and k-anonymity guards exist to prevent, arriving through the
   * one door nobody watched.
   */
  | { kind: 'answered'; result: TurnResultPayload; incomplete: boolean }
  | { kind: 'error'; variant: TurnErrorVariant; code: string; status: number | null };

export interface TurnObservation {
  /** The most recent poll body, or `null` before the first one lands. */
  poll: TurnPollResponse | null;
  /** Set when the POST or a GET failed at the transport layer. */
  transportError: TransportFailure | null;
  /** The operator pressed Stop. A client abort cannot stop the worker. */
  stopped: boolean;
  /** `Date.now()` at the moment the POST was issued; `null` when idle. */
  askedAt: number | null;
  /** Injected, never read from the clock inside this module. */
  now: number;
}

/**
 * 🔴 THE CLOCK IS AN ARGUMENT, NOT AN AMBIENT.
 *
 * A previous suite in this build pinned a wall-clock instant against code that
 * read `Date.now()`; it passed before 12:15Z and failed after, and its green
 * was evidence about the clock rather than about the control. `now` is passed
 * in so every assertion below is a statement about the STATE MACHINE.
 */
export function deriveScreenState(observation: TurnObservation): AgentScreenState {
  const { poll, transportError, stopped, askedAt, now } = observation;

  // A transport failure outranks a stale poll body: the screen must not keep
  // animating a spinner over a request that is never coming back.
  if (transportError !== null) {
    return {
      kind: 'error',
      variant: transportErrorVariant(transportError.status),
      code: transportError.code,
      status: transportError.status,
    };
  }

  if (stopped) return { kind: 'stopped' };
  if (askedAt === null) return { kind: 'empty' };

  // A TERMINAL SERVER STATE OUTRANKS THE CLOCK. A turn the worker finished is
  // an answer however late it arrived, so these are tested before the give-up
  // deadline below and a slow-but-successful turn is never reported as a
  // timeout.
  if (poll?.status === 'failed') {
    return {
      kind: 'error',
      variant: 'turn_failed',
      code: poll.error ?? 'turn_failed',
      status: null,
    };
  }

  if (poll?.status === 'done' && poll.result !== null) {
    return terminalState(poll.result);
  }

  /**
   * 🔴 THE GIVE-UP CHECK MUST PRECEDE THE `poll === null` RETURN.
   *
   * It used to sit BELOW it. A turn whose FIRST poll never landed therefore
   * kept `poll === null` for ever, returned `loading` for ever, and could never
   * reach this line — so the one state that exists to end a stuck turn was
   * unreachable on exactly the path that produces one.
   *
   * The screen was not merely wrong, it was inert. `useAgentTurn` computes its
   * own `gaveUp` from the same deadline and drops `busy` at the give-up, which
   * removes the Stop button. The operator was then left with an animating
   * ladder that could neither finish nor be cancelled, and no error to retry
   * from. Two derivations of one fact disagreed, and the screen followed the
   * one that never terminates.
   *
   * Reproduced on DEV 2026-08-27: the worker recorded the turn `declined` at
   * 14:00:05; 90 seconds later the page still showed "Screening the question"
   * with no Stop. The trigger there was a poll loop parked on `document.hidden`
   * with no visibility transition to wake it, but the trigger is not the point
   * — ANY first-poll failure reaches this state, and a real operator reaches it
   * by leaving the tab for longer than the deadline.
   */
  const elapsedMs = Math.max(0, now - askedAt);
  if (elapsedMs > POLL_GIVE_UP_MS) {
    return { kind: 'error', variant: 'timed_out', code: 'client_gave_up', status: null };
  }

  if (poll === null) return { kind: 'loading' };

  return {
    kind: 'running',
    phase: poll.phase,
    iteration: poll.iteration,
    toolCallsDone: poll.tool_calls_done,
    tool: poll.tool,
    elapsedMs,
    // Non-prod only. See SLOW_TURN_HINT_MS.
    slowHint: elapsedMs >= SLOW_TURN_HINT_MS && poll.environment !== 'prod',
    environment: poll.environment,
  };
}

function transportErrorVariant(status: number | null): TurnErrorVariant {
  if (status === 403) return 'forbidden';
  // ⚠️ UNREACHABLE IN PHASE A, AND DELIBERATELY SO. Spec 10.6(4): the daily
  // budget ships in RECORD-ONLY mode with a generous ceiling until two weeks of
  // real traffic set it, so nothing answers 429 yet. The arm exists because the
  // change that ARMS the budget is the change that arms this rendering — but it
  // is NOT in the state test matrix, because an untestable UI state presented
  // as a working feature is dead code that looks like one.
  if (status === 429) return 'rate_limited';
  return 'transport';
}

/**
 * The two gate-pattern reasons are indistinguishable to the operator.
 *
 * `input-gate.ts:84-88`: "Identical for every reason ON PURPOSE: telling an
 * operator WHICH pattern fired is a probing oracle for the roster." Anything
 * that reaches the browser and differs between the two hands that back.
 */
export function collapseGateReason(raw: string | null | undefined): string {
  if (raw === 'member_identifier' || raw === 'member_name') return 'member_reference';
  return raw ?? 'refused';
}

function terminalState(result: TurnResultPayload): AgentScreenState {
  // The pre-model input gate refused: ZERO model requests were made, the
  // question never left the estate, and this is NOT an error the operator did
  // wrong by asking — it is the product working (spec 12).
  // 🔴 AN INTERNAL FAULT IS NOT A REFUSAL, AND MUST NOT BE SHOWN AS ONE.
  //
  // `input-gate.ts` is fail-closed on its own bugs and says so: "Any thrown
  // exception anywhere in steps 1-4 REFUSES. `GateReason` is a CLOSED
  // four-member union, so AN INTERNAL FAULT IS REPORTED AS `probe_unavailable`
  // ... rather than widening the union." That is the right SERVER decision —
  // `allow` is false and no request is made.
  //
  // Treating it like the other three on the CLIENT is what was wrong. It
  // rendered `RefusedPanel`, which tells the operator to "Remove any member
  // name, e-mail address, phone number or id and ask again" — for a fault they
  // did not cause and cannot fix by rewording. And `RefusedPanel` takes no
  // `onRetry` at all, while its sibling `ErrorPanel` does: the one turn worth
  // retrying was routed to the one panel that structurally cannot offer it.
  //
  // Rewording the refusal was the wrong fix; it would keep a backend fault
  // filed as an operator error. `turn_failed` already reads "The turn failed
  // ... Nothing was answered" and is `retryable: true`.
  //
  // The label stays `probe_unavailable` on the wire and in the audit row — the
  // gate's closed union is not widened, and support can still resolve it by
  // turn_id.
  if (result.gate_reason === 'probe_unavailable') {
    return {
      kind: 'error',
      variant: 'turn_failed',
      code: 'probe_unavailable',
      status: null,
    };
  }

  if (result.gate_allow === false || result.outcome === 'refused') {
    return {
      kind: 'refused',
      // 🔴 COLLAPSED AT THE BOUNDARY, because deleting the PROSE was not enough.
      //
      // The first attempt removed the distinguishing copy and then printed the
      // raw code in a monospace line — so `member_identifier` and `member_name`
      // were still distinguishable by reading the screen. The oracle survived in
      // a different font, and the commit claimed it was closed.
      //
      // The two gate PATTERNS now share ONE operator-facing token. The true code
      // still reaches `core.admin_audit_log` server-side, so support can resolve
      // it by turn_id — which is where a code that must not be shown belongs.
      reasonCode: collapseGateReason(result.gate_reason),
      result,
    };
  }
  if (result.decline !== null) {
    return { kind: 'declined', decline: result.decline, result };
  }
  return {
    kind: 'answered',
    result,
    incomplete: result.outcome === 'deadline_exceeded',
  };
}

/**
 * The decline panel's heading.
 *
 * 🔴 "Not answered" is FALSE above a table. When the turn served rows before
 * the model declined, the figures ARE on the screen; what is missing is the
 * model's sentence about them. The wording is neutral on WHY the model wrote
 * none — its own message, printed under the heading, says that.
 */
export function declinedHeading(kind: 'clarify' | 'decline', tablesBelow: boolean): string {
  if (tablesBelow) return 'The model wrote no summary — your figures are in the table below';
  return kind === 'clarify' ? 'One clarification needed' : 'Not answered, on purpose';
}

/**
 * A short, operator-readable line for a decline or refusal reason code.
 *
 * The map is CLOSED and the fallback echoes the code itself rather than
 * inventing prose: a reason code this file has not been taught about must show
 * up as an unfamiliar token an operator can search for, never as a confident
 * sentence about something the surface does not understand.
 */
const REASON_COPY: Record<string, string> = {
  // 🔴 THE TWO GATE-PATTERN CODES ARE DELIBERATELY ABSENT FROM THIS MAP.
  //
  // `input-gate.ts:84-88` states the control in its own words: "What the
  // operator is shown. Identical for every reason ON PURPOSE: telling an
  // operator WHICH pattern fired is a probing oracle for the roster." The
  // backend therefore sends ONE canned string, `GATE_REFUSAL_MESSAGE`, for
  // every gate refusal.
  //
  // This map then replaced it with per-code prose — "an e-mail, a phone number
  // or an id" versus "looks like it names a member" — handing back exactly the
  // discrimination the backend had removed. Somebody probing the roster could
  // tell a name hit from an identifier hit by reading the screen.
  //
  // The codes still travel, and `RefusedPanel` still shows the raw token in its
  // monospace line so support can search for it. What is gone is the PROSE that
  // differs between them.
  too_long: 'The question was longer than the gate accepts. Try a shorter one.',
  no_metric:
    'No metric in the catalogue answers this question. The suggestions below are ' +
    'the closest ones you are permitted to read.',
  out_of_scope: 'This is outside what the metric catalogue covers.',
  grain_too_fine:
    'The requested grain is finer than the disclosure policy permits for this ' +
    'metric. Ask for a wider window or a coarser grain.',
  window_too_short:
    'The requested window is shorter than this metric’s minimum. Ask for a wider one.',
  freshness: 'The underlying data is older than this metric’s freshness contract.',
};

export function reasonCopy(code: string): string {
  return REASON_COPY[code] ?? `Reason code: ${code}`;
}

/**
 * Copy for the five error renderings. Separate from `reasonCopy` because a
 * TRANSPORT failure and a REFUSAL are different facts with different remedies,
 * and spec 16.2 forbids collapsing them into one banner.
 */
export function errorCopy(state: {
  variant: TurnErrorVariant;
  code: string;
  status: number | null;
}): { title: string; body: string; retryable: boolean } {
  switch (state.variant) {
    case 'forbidden':
      return {
        title: 'You do not hold the key this needs',
        body:
          'The agent transport requires the `agent.chat` permission key, and each ' +
          'metric additionally requires the key of the page that already shows it. ' +
          'Ask a Super Admin for the key named in the response.',
        retryable: false,
      };
    case 'rate_limited':
      return {
        title: 'Daily question budget reached',
        body: 'The daily cap for this account has been reached. It resets at midnight UTC.',
        retryable: false,
      };
    case 'timed_out':
      // Reached only after the server's own bound (see POLL_GIVE_UP_MS), so no
      // worker can still be running this turn and "may still finish" would be
      // false. It does NOT follow that the server row says so: only a poll GET
      // ends a lost row, and this screen has stopped polling (review of #3273).
      // So the copy claims the worker, never the row.
      return {
        title: 'This screen stopped waiting',
        body:
          'No result reached this screen in more than 13 minutes. By then no ' +
          'worker can still be running this turn, so waiting longer will not ' +
          'help. Ask again.',
        retryable: true,
      };
    case 'turn_failed':
      // The two codes the SERVER writes when no worker can be alive
      // (`turn-liveness.ts`). "The worker ended this turn" would be false for
      // both: the worker never ran, or it stopped without a word.
      if (state.code === 'worker_lost') {
        return {
          title: 'The turn stopped without finishing',
          body:
            'The worker stopped before it delivered an answer, and the server ' +
            'ended the turn (code “worker_lost”). Nothing was answered. Ask again.',
          retryable: true,
        };
      }
      if (state.code === 'never_started') {
        return {
          title: 'The turn never started',
          body:
            'No worker picked up this question in time, usually because the ' +
            'agent was busy (code “never_started”). Nothing was answered. Ask again.',
          retryable: true,
        };
      }
      return {
        title: 'The turn failed',
        body: `The worker ended this turn with code “${state.code}”. Nothing was answered.`,
        retryable: true,
      };
    default:
      return {
        title: 'Could not reach the agent',
        body:
          state.status === null
            ? `The request did not complete (${state.code}). Check your connection and retry.`
            : `The API answered HTTP ${state.status} (${state.code}).`,
        retryable: true,
      };
  }
}
